import { useState, useEffect, useMemo } from "react";
import { usePlaidLink } from "react-plaid-link";
import * as Sentry from "@sentry/react";

import { useCollectionDataListener } from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";

const callPlaidApi = async (endpoint, data = {}) => {
  return await fetchOurApi({
    path: `/integrations/plaid/${endpoint}`,
    method: "POST",
    data,
    callback: (response) => response,
  });
};

const createLinkToken = async (accountId) =>
  await callPlaidApi("create-link-token", { accountId });

const exchangePublicToken = async (
  publicToken,
  accountId,
  metadata,
  isReauthentication
) =>
  await callPlaidApi("exchange-public-token", {
    publicToken,
    accountId,
    metadata,
    isReauthentication,
  });

const fetchAccounts = async (accountId) =>
  await callPlaidApi("fetch-accounts", { accountId });

const revokeAccess = async (accountId, integrationId) =>
  await callPlaidApi("revoke-access", { accountId, integrationId });

const refreshTransactions = async (accountId, itemId, integrationId) =>
  await callPlaidApi("refresh-transactions", {
    accountId,
    itemId,
    integrationId,
  });

const createReauthenticationLinkToken = async (accountId, itemId) =>
  await callPlaidApi("create-reauthentication-link-token", {
    accountId,
    itemId,
  });

export const usePlaid = (props = {}) => {
  const { setPlaidError } = props;
  const accountId = getAccountId();

  const [plaidIntegrations, plaidIntegrationsLoading] =
    useCollectionDataListener("integrations", [["type", "==", "plaid"]]);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReauthentication, setIsReauthentication] = useState(false);

  const activeIntegrations = useMemo(
    () =>
      plaidIntegrations.filter(
        (integration) => !integration.revokedAt && !integration.archived
      ),
    [plaidIntegrations]
  );

  useEffect(() => {
    const loadAccounts = async () => {
      if (!activeIntegrations.length) {
        setAccounts([]);
        return;
      }

      setAccountsLoading(true);
      try {
        const response = await fetchAccounts(accountId);
        if (response?.error) {
          throw new Error(response.message || "Failed to load bank accounts");
        }
        setAccounts(response?.accounts || []);
      } catch (error) {
        console.error("Error fetching accounts:", error);
        if (setPlaidError) {
          setPlaidError("Failed to load bank accounts. Please try refreshing.");
        }
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    loadAccounts();
  }, [activeIntegrations, accountId, setPlaidError]);

  const handleLinkTokenError = (error) => {
    if (setPlaidError) {
      setPlaidError("Failed to initialize bank connection. Please try again.");
    }
    console.error("Error creating link token:", error);
    setIsLoading(false);
  };

  const launchPlaidLink = async () => {
    setIsLoading(true);
    setIsReauthentication(false);
    if (setPlaidError) {
      setPlaidError("");
    }

    try {
      const { linkToken: token } = await createLinkToken(accountId);
      setLinkToken(token);
    } catch (error) {
      handleLinkTokenError(error);
    }
  };

  const launchReauthentication = async (itemId) => {
    setIsLoading(true);
    setIsReauthentication(true);
    if (setPlaidError) {
      setPlaidError("");
    }

    try {
      const { linkToken: token } = await createReauthenticationLinkToken(
        accountId,
        itemId
      );
      setLinkToken(token);
    } catch (error) {
      handleLinkTokenError(error);
    }
  };

  const handleTokenExchangeError = (error) => {
    if (setPlaidError) {
      setPlaidError("Failed to complete bank connection. Please try again.");
    }
    console.error("Error exchanging public token:", error);
    setIsLoading(false);
  };

  const handlePlaidSuccess = async (publicToken, metadata) => {
    setIsLoading(true);
    if (setPlaidError) {
      setPlaidError("");
    }

    try {
      await exchangePublicToken(
        publicToken,
        accountId,
        metadata,
        isReauthentication
      );
    } catch (error) {
      handleTokenExchangeError(error);
    } finally {
      setIsLoading(false);
      setIsReauthentication(false);
    }
  };

  const handlePlaidExit = (err, metadata) => {
    const { link_session_id, institution } = metadata || {};
    const { institutionId, name: institutionName } = institution || {};
    const { error_type, error_code } = err || {};

    Sentry.withScope((scope) => {
      scope.setTag("feature", "plaid-integration");
      scope.setTag("plaid_event", "link-exit");
      if (institutionId) {
        scope.setTag("institution_id", institutionId);
      }
      scope.setLevel(err ? "error" : "info");
      scope.setExtra("link_session_id", link_session_id);
      scope.setExtra("institution_name", institutionName);
      scope.setExtra("plaid_error", err);
      scope.setExtra("plaid_metadata", metadata);
      scope.setFingerprint([
        "plaid-integration",
        "exit",
        error_code || "no-error",
        link_session_id || "no-session",
      ]);
      Sentry.captureMessage(
        `Plaid Link exit${
          err ? ` with error: ${error_type}/${error_code}` : ""
        }`
      );
    });

    if (err && setPlaidError) {
      setPlaidError(
        "Bank connection was cancelled or failed. Please try again."
      );
    }
    setLinkToken(null);
    setIsLoading(false);
    setIsReauthentication(false);
  };

  const handleRevokeError = (error) => {
    if (setPlaidError) {
      setPlaidError("Failed to disconnect bank account. Please try again.");
    }
    console.error("Error revoking access:", error);
    setIsLoading(false);
  };

  const revokePlaidAccess = async (integrationId) => {
    if (!integrationId) return;

    setIsLoading(true);
    if (setPlaidError) {
      setPlaidError("");
    }

    try {
      await revokeAccess(accountId, integrationId);
    } catch (error) {
      handleRevokeError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAccountsForItem = (itemId) => {
    return accounts.filter((account) => account.itemId === itemId);
  };

  const handleRefreshError = (error) => {
    console.error("Error refreshing accounts:", error);
    if (setPlaidError) {
      setPlaidError("Failed to refresh bank accounts. Please try again.");
    }
    setAccountsLoading(false);
  };

  const refreshAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await fetchAccounts(accountId);
      if (response?.error) {
        throw new Error(response.message || "Failed to load bank accounts");
      }
      setAccounts(response?.accounts || []);
    } catch (error) {
      handleRefreshError(error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  useEffect(() => {
    if (linkToken && ready && open) {
      open();
    }
  }, [linkToken, ready, open]);

  const refreshTransactionsForItem = async (itemId, integrationId) =>
    await refreshTransactions(accountId, itemId, integrationId);

  return {
    plaidIntegrations,
    plaidIntegrationsLoading,
    activeIntegrations,
    accounts,
    accountsLoading,
    linkToken,
    isLoading,

    launchPlaidLink,
    launchReauthentication,
    handlePlaidSuccess,
    handlePlaidExit,
    revokePlaidAccess,
    getAccountsForItem,
    refreshAccounts,

    createLinkToken,
    exchangePublicToken,
    fetchAccounts,
    revokeAccess,
    refreshTransactions: refreshTransactionsForItem,
  };
};
